import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import {
	addLinkAssetPart,
	removePartLink,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useAssetPartsGridActionsAndButtons = ({
	gridRef,
	searchStatistics,
	searchAssetParts,
	setRemovePartError,
	includeDescendants,
}: {
	gridRef: GridApi | null;
	searchStatistics: Function;
	searchAssetParts: Function;
	setRemovePartError: (err: string) => void;
	includeDescendants: boolean;
}) => {
	const {
		state: { assetSearchId, listAssetParts, rowSelected },
		actions: {
			setFirstTime,
			setPartSearch,
			setDataPartSelected,
			setListAssetParts,
		},
	} = useAssetDetailsContext();
	const [searchParams] = useSearchParams();
	const [refreshTrigger, setRefreshTrigger] = useState(1);
	const isMountRef = useRef(true);

	// BUTTONS STATES
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			setFirstTime(true);
			setSaveButton(false);
			setDeleteOpen(false);
			setPartSearch("");
			setDataPartSelected(undefined);
			searchAssetParts(includeDescendants);
			searchStatistics();
		}
	}, [searchParams, assetSearchId, includeDescendants]);

	const addRow = useCallback(() => {
		setSaveButton(true);
		const newData = { id: -1, AssetId: assetSearchId };
		const currentData = listAssetParts ? listAssetParts : [];
		const data = [newData, ...currentData];
		setListAssetParts(data);
	}, [listAssetParts, assetSearchId]);

	const removeSelected = useCallback(async () => {
		if (!gridRef) return;

		const selectedRowNodes = gridRef.getSelectedNodes();

		try {
			if (selectedRowNodes?.length) {
				const partsFromOtherAssets = selectedRowNodes.filter(
					(node) => Number(node.data.AssetId) !== Number(assetSearchId),
				);

				if (partsFromOtherAssets.length > 0) {
					const partsList = partsFromOtherAssets
						.map((node) => `${node.data.Name} (${node.data.AssetName})`)
						.join(", ");
					setRemovePartError(
						`Cannot remove parts linked to other assets:\n${partsList}\n\nThese parts must be removed from their respective assets.`,
					);
					setDeleteOpen(false);
					return;
				}

				const partNamesToRemove = selectedRowNodes.map(
					(node) => node.data.Name,
				);

				const result = await removePartLink(
					assetSearchId as any,
					partNamesToRemove,
				);

				if (!result.ok) {
					setRemovePartError(result.message);
					throw new Error(result.message);
				}
			} else if (rowSelected) {
				if (Number(rowSelected.AssetId) !== Number(assetSearchId)) {
					setRemovePartError(
						`Cannot remove part "${rowSelected.Name}" linked to "${rowSelected.AssetName}".\n\nThis part must be removed from that asset.`,
					);
					setDeleteOpen(false);
					return;
				}

				const result = await removePartLink(
					assetSearchId as any,
					rowSelected.Name,
				);

				if (!result.ok) {
					setRemovePartError(result.message);
					return;
				}
			}

			setRefreshTrigger((prev) => prev + 1);
		} catch (error) {
			console.error("Error removing parts:", error);
		}
	}, [assetSearchId, gridRef, rowSelected]);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		addRow,
		removeSelected,
		saveButton,
		deleteOpen,
		setSaveButton,
		setDeleteOpen,
	};
};
